import React, { useEffect, useState } from 'react'
import { toLower } from 'ramda'

import { Sentiment } from 'sidecar-components/types'
import { Bubble } from 'sidecar-components/molecules/Bubble'
import {
  Button,
  ButtonSentiment,
  ButtonSize,
  buttonStyles,
} from 'sidecar-components/atoms/Button'
import {
  ButtonContainer,
  ContainerAlignment,
} from 'sidecar-components/atoms/ButtonContainer'
import { Card } from 'sidecar-components/atoms/Card'
import { Icon, IconSize, IconType } from 'sidecar-components/atoms/Icon'
import { LinkType } from 'sidecar-components/atoms/Link'
import { Modal } from 'sidecar-components/molecules/Modal'
import { Row } from 'sidecar-components/atoms/Row'
import { toCamelCase, getProviderLogoUrl } from 'sidecar-utilities'

import {
  checkSameFacilitiesAndProviders,
  getNamesAsString,
  getButtonCardText,
  getModalTitle,
  labsAndImagingText,
} from './utils'
import { LearnMoreButton } from './LearnMoreButton'
import { Provider, GenericLinkComponent } from './PreCheckedProviders.types'

interface PreCheckedProviderModalProps {
  borderOverflow?: boolean
  /** Array of facility providers */
  facilityProviders?: Provider[]
  /** Whether the sidecar code type is Professional */
  isProfessional: boolean
  /** Whether the sidecar code type is Facility */
  isFacility: boolean
  /** CSS class names for link styling */
  linkStyles: string
  /** Individual provider data */
  provider?: Provider
  /** Array of professional providers */
  professionalProviders?: Provider[]
  /** Type of component usage context */
  type?: string
  /** Whether there are multiple category names */
  hasMultipleCategoryNames: boolean
  /** Pre-checked category name from Contentful */
  preCheckedCategoryName?: string
  /** Whether to show the pre-check title */
  showPreCheckTitle: boolean
  /**
   * Link component to use for external links (Next.js Link, React Router Link,
   * etc.)
   */
  linkComponent?: GenericLinkComponent
  /** Whether the component is used in Premember */
  premember?: boolean
  /** When true, hides the external "Learn more" link for no-swipe providers */
  hideNoSwipeLink?: boolean
}

export const PreCheckedProviderModal: React.FC<
  PreCheckedProviderModalProps
> = ({
  borderOverflow = false,
  facilityProviders = [],
  isProfessional,
  isFacility,
  linkStyles,
  provider = {} as Provider,
  professionalProviders = [],
  type = '',
  hasMultipleCategoryNames,
  preCheckedCategoryName = '',
  showPreCheckTitle,
  linkComponent,
  premember = false,
  hideNoSwipeLink = false,
}) => {
  // Icon size mapping for consistent sizing
  const iconSizeMap = {
    [ButtonSize.LARGE]: IconSize.MD,
    [ButtonSize.MEDIUM]: IconSize.SM,
    [ButtonSize.SMALL]: IconSize.XS,
  }

  // Custom link button component that uses the passed linkComponent for external links
  const CustomLinkButton: React.FC<{
    path: string
    pathType?: any
    children: string
    testId?: string
    sentiment?: any
    size?: any
    icon?: string
    iconPosition?: 'before' | 'after'
  }> = ({
    path,
    children,
    testId,
    sentiment,
    size,
    icon,
    iconPosition,
    ...props
  }) => {
    if (linkComponent) {
      // Use the custom link component when provided
      const LinkComponent = linkComponent
      return (
        <LinkComponent
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonStyles({
            busy: false,
            children,
            disabled: false,
            icon,
            sentiment,
            size,
            width: 'flex',
          })}
          data-testid={testId}
        >
          {iconPosition === 'before' && icon && (
            <Icon name={icon} size={iconSizeMap[size] || IconSize.SM} />
          )}
          <span className="truncate">{children}</span>
          {iconPosition === 'after' && icon && (
            <Icon name={icon} size={iconSizeMap[size] || IconSize.SM} />
          )}
        </LinkComponent>
      )
    }
    // Fall back to the regular Button only when no linkComponent is provided
    return (
      <Button
        path={path}
        pathType={LinkType.EXTERNAL}
        testId={testId}
        sentiment={sentiment}
        size={size}
        icon={icon}
        iconPosition={iconPosition}
        {...props}
      >
        {children}
      </Button>
    )
  }

  // ----- Variables -----
  const [isOpen, setIsOpen] = useState(false)
  const [logoFailed, setLogoFailed] = useState(false)
  const {
    categoryName,
    directBill = false,
    iconImage = '',
    providerName,
    url = '',
    description,
  } = provider
  useEffect(() => setLogoFailed(false), [iconImage])
  const iconUrl =
    iconImage && !logoFailed
      ? getProviderLogoUrl(iconImage, 'prechecked-provider')
      : ''
  const sameFacilityAndProviders = checkSameFacilitiesAndProviders({
    providerName: providerName,
    listToCheck: facilityProviders,
  })
  const showBubble =
    isFacility && professionalProviders.length > 0 && !sameFacilityAndProviders
  const showPreCheckedFacilities =
    isProfessional && facilityProviders.length > 0 && !sameFacilityAndProviders
  const pluralizedTitle = isProfessional ? 'providers' : 'facilities'
  const title = isProfessional ? 'provider' : 'facility'
  const testId = toCamelCase(providerName)
  const showProviderDescription =
    type === 'careSearch' &&
    hasMultipleCategoryNames &&
    professionalProviders.length > 1

  // ----- JSX elements -----
  const viewWebsiteBtn = (
    <ButtonContainer align={ContainerAlignment.CENTER}>
      <CustomLinkButton
        testId={`btn_visitWebsite_${testId}`}
        icon="link"
        iconPosition="before"
        path={url}
        pathType={LinkType.EXTERNAL}
        size={ButtonSize.LARGE}
      >
        {/* directBill = "no swipe" */}
        {directBill ? 'View details' : 'Visit website'}
      </CustomLinkButton>
    </ButtonContainer>
  )

  const buttonCard = (
    <Card className="mt-1">
      <p className="sh-body text-center">{getButtonCardText(directBill)}</p>
      {viewWebsiteBtn}
    </Card>
  )

  const beforeSchedulingBubble = () => {
    return (
      <Bubble
        id={`visitWebsite_${testId}`}
        canDismiss={false}
        icon="exclamation"
        sentiment={Sentiment.NOTIFY}
        message={`${getNamesAsString({
          providers: professionalProviders,
          conjunction: 'and',
        })} ${
          professionalProviders.length > 1 ? 'are' : 'is'
        } the pre-checked ${
          professionalProviders.length > 1 ? 'providers' : 'provider'
        } affiliated 
          with ${providerName}. If you need to schedule a surgery or procedure, ask your 
          ${getNamesAsString({
            providers: professionalProviders,
            conjunction: 'or',
          })} doctor to schedule it at ${providerName}.`}
        title="Before scheduling"
      />
    )
  }

  const aboutSectionContent = (
    <React.Fragment>
      <h4>{`About pre-checked ${pluralizedTitle}`}</h4>
      <p className="sh-body-sm">
        If your deductible has been met or it does not apply:
      </p>
      <ul className="sh-body-sm sh-ul-sm">
        <li>
          You won't pay anything for{' '}
          {/^(careSearch)$/.test(type)
            ? preCheckedCategoryName
            : 'this service'}{' '}
          at this pre-checked {title}.
        </li>
      </ul>

      <p className="sh-body-sm">
        If your deductible has not been met and it applies:
      </p>
      <ul className="sh-body-sm sh-ul-sm">
        <li>
          The pre-checked {title}
          {`\u2019`}s price for this service is usually at or below the average
          cash price, in which case the Benefit Amount will apply to your
          deductible, and you will be responsible to the pre-checked {title} for
          its charge.
        </li>
        <li>
          If the pre-checked {title} charges more than the average cash price,
          then the {title}
          {`\u2019`}s charge will apply to the deductible. In such case, you
          will be responsible for the entire amount of the pre-checked {title}
          {`\u2019`}s charge, which may exceed the Benefit Amount shown in the
          member portal.
        </li>
      </ul>

      {/* Only display the following for direct bill providers */}
      {directBill ? (
        <>
          <h4>Other important notes</h4>
          <p className="sh-body-sm">
            To find providers for other services,{' '}
            {labsAndImagingText(toLower(categoryName))}, conduct a new search.
          </p>
          {/* the button container is needed for the active button states to display correctly */}
          {!premember && (
            <ButtonContainer>
              <LearnMoreButton
                sentiment={ButtonSentiment.SECONDARY}
                testId={testId}
                linkComponent={linkComponent}
              />
            </ButtonContainer>
          )}

          <h4 className="flex items-center gap-2">
            <Icon
              name="noSwipe"
              size={IconSize.SM}
              className="color-iconAccent-default"
            />{' '}
            No swipe {title}
          </h4>
          <p className="sh-body-sm">Do not pay this {title} directly.</p>
          <ul className="sh-body-sm sh-ul-sm">
            <li>Ask this {title} to bill Sidecar Health.</li>
            <li>
              Show {title} your digital member ID so they can bill Sidecar
              Health.
            </li>
            <li>
              You won{`\u2019`}t need to collect a medical invoice from this{' '}
              {title}.
            </li>
          </ul>
          {!premember && !hideNoSwipeLink && (
            <CustomLinkButton
              path="https://sidecarhealth.com/noswipe"
              pathType={LinkType.EXTERNAL}
              sentiment={ButtonSentiment.SECONDARY}
              testId="btn_learnAboutNoSwipe"
            >
              Learn more
            </CustomLinkButton>
          )}
        </>
      ) : (
        <>
          <h4>Other important notes:</h4>
          <ul className="sh-body-sm sh-ul-sm">
            <li>
              Itemized invoices for this service must include the name of the
              pre-checked {title}.
            </li>
            <li>
              To find pre-checked {pluralizedTitle} for other services,{' '}
              {labsAndImagingText(toLower(categoryName))}, conduct a new search.
            </li>
            <li>Ask for discounted rates.</li>
            <li>
              For services costing more than $2,500, contact Member Care to
              increase swipe limits.
            </li>
            <li>
              Because {title} prices can change, {pluralizedTitle} identified as
              pre-checked are also subject to change.
            </li>
          </ul>
          {!premember && (
            <ButtonContainer>
              <LearnMoreButton
                sentiment={ButtonSentiment.SECONDARY}
                testId={testId}
                linkComponent={linkComponent}
              />
            </ButtonContainer>
          )}
        </>
      )}
    </React.Fragment>
  )

  const preCheckedFacilities = () => {
    return (
      <React.Fragment>
        <h4>Pre-checked facilities</h4>
        <p className="sh-body-sm">
          {`If you need a surgery or procedure, ask for it to be scheduled at ${getNamesAsString(
            {
              providers: facilityProviders,
              conjunction: 'or',
            },
          )}, the pre-checked facility related to ${
            type === 'careDetail'
              ? 'this service'
              : preCheckedCategoryName || toLower(categoryName)
          }.`}
        </p>
      </React.Fragment>
    )
  }

  return (
    <Modal
      size="lg"
      title={getModalTitle(directBill, providerName)}
      onOpenChange={setIsOpen}
      open={isOpen}
      trigger={
        <Row
          borderOverflow={borderOverflow}
          childrenClass={linkStyles}
          testId={`btn_precheckedRow_${testId}`}
          leftMedia={
            <div
              data-testid={`info_precheckedRowImage_${testId}`}
              className="w-10 h-10 mr-0.5"
            >
              {iconUrl && (
                <img
                  src={iconUrl}
                  alt={providerName}
                  className="w-full h-full object-contain"
                  onError={() => setLogoFailed(true)}
                />
              )}
            </div>
          }
        >
          {!!showPreCheckTitle && (
            <div className="sh-body-sm sh-strong flex items-center gap-2">
              <Icon
                name="verify"
                size={IconSize.SM}
                type={IconType.SYSTEM}
                className="color-iconAccent-default"
              />{' '}
              Pre-checked provider
            </div>
          )}

          {providerName}

          {showProviderDescription && (
            <p
              data-testid={`info_precheckedRowDescription_${testId}`}
              className="sh-body-sm"
            >
              {description}
            </p>
          )}
          {!!directBill && (
            <div className="flex items-center gap-2">
              <Icon
                className="color-iconAccent-default"
                name="noSwipe"
                size={IconSize.SM}
              />
              <span className="sh-body-xs">No swipe</span>
            </div>
          )}
        </Row>
      }
    >
      {/* Learn more card with view provider/facility website button; only show if there is a url */}
      {!!url && buttonCard}
      {/* Before scheduling bubble for facilities only */}
      {showBubble && beforeSchedulingBubble()}
      {/* More about Prechecked Providers section; shown for both providers and facilities */}
      {aboutSectionContent}
      {/* Prechecked facilities section; shown for providers only when there is at least one facility */}
      {showPreCheckedFacilities && preCheckedFacilities()}
    </Modal>
  )
}
